import { Injectable } from '@angular/core';
import cloneDeep from 'lodash/cloneDeep';
import menuItemsObj from 'src/assets/menu/menu.json';
import { LoggerService } from '../logger/logger.service';
import { MenuChange } from 'src/app/enums/menuChange.enum';
import {
	SecurityAdvisor,
	EventTypes,
	WindowsHello,
	WifiSecurity,
} from '@lenovo/tan-client-bridge-le';
import { VantageShellService } from '../vantage-shell/vantage-shell.service';
import { CommonService } from '../common/common.service';
import { LocalStorageKey } from 'src/app/enums/local-storage-key.enum';
import { BehaviorSubject, Subscription } from 'rxjs';
import { AppNotification } from 'src/app/data-models/common/app-notification.model';
import { AdPolicyEvent } from 'src/app/enums/ad-policy-id.enum';
import { AdPolicyService } from '../ad-policy/ad-policy.service';
import { DashboardService } from '../dashboard/dashboard.service';
import { SmartAssistCapability } from 'src/app/data-models/smart-assist/smart-assist-capability.model';
import { SmartAssistService } from '../smart-assist/smart-assist.service';
import { FeatureToggleService } from '../feature-toggle/feature-toggle.service';
import { UrlTree, Router } from '@angular/router';
import { DeviceService } from '../device/device.service';
import { ModernPreloadService } from 'src/app/services/modern-preload/modern-preload.service';
import { DeviceSettingsFeatureToggle } from 'src/app/enums/feature-toggle.enum';
import { IMenuItem } from './menu-item.interface';
import { DeviceSettingsConfigService } from './device-settings-config/device-settings-config.service';
import { ActiveProtectionSystemSection } from 'src/app/presenters/user-cases/smart-assist-page/active-protection-system-section/active-protection-system-section';
import { EMachineType } from '../../enums/machine-type.enum';
import { IntelligentSecuritySection } from 'src/app/presenters/user-cases/smart-assist-page/intelligent-security-section/intelligent-security-section';
import { IntelligentSecurityAIOService } from 'src/app/services/smart-assist/intelligent-security-AIO/intelligent-security-AIO.service';
import { EDeviceSettingsPages } from 'src/app/enums/device-settings-page.enum';
import { DisplayCameraPage } from 'src/app/presenters/user-cases/display-camera-page/display-camera-page';
import { WifiSecurityPresenterService } from 'src/app/presenters/user-cases/wifi-security/wifi-security-presenter';
import { HardwareScanService } from 'src/app/modules/hardware-scan/services/hardware-scan.service';
import { SystemUpdatePresenterService } from 'src/app/presenters/user-cases/system-update/system-update-presenter';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	menuItems: IMenuItem[] = menuItemsObj.menuItems as IMenuItem[];
	menu: IMenuItem[] = [];
	windowsHello: WindowsHello;
	securityAdvisor: SecurityAdvisor;
	replaySubscription: Subscription;
	subscription: Subscription;
	private isSmartAssistAvailable = false;
	lastFeatureVersion = 0;
	defaultRoute: UrlTree;
	defaultRouteSubject: BehaviorSubject<UrlTree>;
	intelligentScreenFeatureToggleVisibility: boolean;
	activeProtectionSystemFeatureToggleVisibility: boolean;
	intelligentSecurityFeatureToggleVisibility: boolean;

	constructor(
		private logger: LoggerService,
		private vantageShellService: VantageShellService,
		private adPolicyService: AdPolicyService,
		public dashboardService: DashboardService,
		private smartAssist: SmartAssistService,
		private commonService: CommonService,
		public modernPreloadService: ModernPreloadService,
		private featureToggleService: FeatureToggleService,
		private router: Router,
		private deviceService: DeviceService,
		private deviceSettingsConfigService: DeviceSettingsConfigService,
		private activeProtectionSystemSection: ActiveProtectionSystemSection,
		private intelligentSecuritySection: IntelligentSecuritySection,
		private intelligentSecurityAIOService: IntelligentSecurityAIOService,
		private displayCameraPage: DisplayCameraPage,
		private wifiSecurityPresenter: WifiSecurityPresenterService,
		private hardwareScanService: HardwareScanService,
		private systemUpdatePresenter: SystemUpdatePresenterService
	) {
		this.replaySubscription = this.commonService.replayNotification.subscribe(
			(notification: AppNotification) => {
				this.onNotification(notification);
			}
		);

		this.subscription = this.commonService.notification.subscribe(
			(notification: AppNotification) => {
				this.onNotification(notification);
			}
		);

		this.defaultRouteSubject = new BehaviorSubject(this.router.parseUrl('/'));
	}

	private onNotification(notification: AppNotification) {
		if (notification) {
			switch (notification.type) {
				case MenuChange.MenuItemChange:
					this.chooseDefaultRoute(this.menu);
					break;
				default:
					break;
			}
		}
	}

	getMenuItems() {
		return this.menuItems;
	}

	initGetMenuItems(): void {
		this.initDeviceSettingsMenu();
		this.initWifiMenu();
		this.initSmartAssistMenu();
		this.initSystemUpdateItemMenu();
		this.initHardwareScanMenu();
	}
	private async initWifiMenu() {
		this.wifiSecurityPresenter.isVisible().then((isVisible: boolean) => {
			this.initializeWiFiItem(this.menuItems, isVisible);
		});
	}
	private async initDeviceSettingsMenu() {
		const machineType = await this.deviceService.getMachineType();
		if (machineType === EMachineType.Desktop) {
			await this.updateMenuForDesktop(this.menuItems);
		} else {
			await this.updateMenuForLaptop(this.menuItems);
			await this.initializeDeviceSettingsMenuItems();
		}
	}

	private async initHardwareScanMenu() {
		await this.updateHardwareScanMenu(this.menuItems);
	}

	private async updateHardwareScanMenu(menu: IMenuItem[]) {
		const HardwareScanMenu = menu.find((item) => item.id === 'hardware-scan');

		// Use the value in "pageTable" from featureToggleService to defined isSupported property
		HardwareScanMenu.isSupported = Boolean(
			this.featureToggleService.isAvailableWithFullName(HardwareScanMenu.featureTag)
		);

		// Use the isAvailable function to define if the menu needed be shown
		HardwareScanMenu.hide = !(await this.hardwareScanService.isAvailable());
		this.updateMenu();
	}

	private async initializeDeviceSettingsMenuItems() {
		this.logger.info('initializeDeviceSettingsMenuItems: enter');
		this.initDisplayAndCameraMenuItem();
		const menuItemsAvailabilities =
			await this.deviceSettingsConfigService.getLaptopMenuItemsAvailabilities();
		menuItemsAvailabilities.map((item) => {
			this.updateDeviceSettingsMenuItem(item.id, !item.visibility);
		});

		this.logger.info('initializeDeviceSettingsMenuItems: leave');
	}

	private async initDisplayAndCameraMenuItem() {
		const isDisplayAndCameraVisible = await this.displayCameraPage.isVisible();
		this.updateDeviceSettingsMenuItem('display-camera', !isDisplayAndCameraVisible);
	}

	private updateDeviceSettingsMenuItem(subItemId: string, hide: boolean) {
		const subItem = this.getDeviceSettingsSubItem(subItemId);
		if (!subItem) {
			return;
		}
		subItem.hide = hide;
		this.updateMenu();
	}

	private getDeviceSettingsSubItem(subItemId: string): IMenuItem {
		const deviceSettingsMenu = this.getDeviceSettingsMenu(this.menuItems);
		if (!deviceSettingsMenu) {
			return;
		}
		const deviceSettingsSubItem = deviceSettingsMenu.subitems.find(
			(item) => item.id === subItemId
		);
		return deviceSettingsSubItem;
	}

	private getDeviceSettingsMenu(menu: IMenuItem[]): IMenuItem {
		const deviceMenu = menu.find((item) => item.id === 'device');
		if (!deviceMenu || !deviceMenu.subitems || deviceMenu.subitems.length < 1) {
			return;
		}
		const deviceSettingsMenu = deviceMenu.subitems.find(
			(item) => item.id === 'device-settings'
		);
		if (
			!deviceSettingsMenu ||
			!deviceSettingsMenu.subitems ||
			deviceSettingsMenu.subitems.length < 1
		) {
			return;
		}
		return deviceSettingsMenu;
	}

	private updateMenuForLaptop(menu: IMenuItem[]) {
		const deviceSettingsMenu = this.getDeviceSettingsMenu(menu);
		if (!deviceSettingsMenu) {
			return;
		}
		this.hideDesktopMenuItems(deviceSettingsMenu);
		this.hideLaptopMenuItems(deviceSettingsMenu, false);
	}

	private hideDesktopMenuItems(deviceSettingsMenu) {
		const devicesettingsDetailMenu = deviceSettingsMenu.subitems.find(
			(item) => item.id === 'device-settings'
		);
		if (devicesettingsDetailMenu) {
			devicesettingsDetailMenu.hide = true;
		}
	}

	private async updateMenuForDesktop(menu: IMenuItem[]) {
		const deviceSettingsMenu = this.getDeviceSettingsMenu(menu);
		if (!deviceSettingsMenu) {
			return;
		}

		await this.showDesktopMenuItems(deviceSettingsMenu);
		this.hideLaptopMenuItems(deviceSettingsMenu, true);
		this.updateMenu();
	}

	private async showDesktopMenuItems(deviceSettingsMenu: IMenuItem) {
		const menuVisibility =
			await this.deviceSettingsConfigService.getDesktopMenuItemVisibility();

		const devicesettingsDetailMenu = deviceSettingsMenu.subitems.find(
			(item) => item.id === 'device-settings'
		);
		if (devicesettingsDetailMenu) {
			devicesettingsDetailMenu.hide = !menuVisibility;
		}
		this.updateMenu();
	}

	private hideLaptopMenuItems(deviceSettingsMenu: IMenuItem, hide: boolean) {
		const nonDesktopMenuItems = ['power', 'audio', 'display-camera', 'input-accessories'];

		deviceSettingsMenu.subitems.forEach((item) => {
			if (nonDesktopMenuItems.includes(item.id)) {
				item.hide = hide;
			}
		});
	}

	private updateMenu() {
		this.applyADPolicy(this.menuItems);
		this.menu = this.configureMenuPath(this.menuItems);
		this.notifyMenuChange(this.menu);
	}

	private initializeWiFiItem(menu: IMenuItem[], isVisible: boolean) {
		this.logger.info('initializeWiFiItem: enter');
		this.updateWiFiMenu(menu, isVisible);
		this.logger.info('initializeWiFiItem: leave');
	}

	private async initSystemUpdateItemMenu() {
		this.updateSystemUpdatesMenu(this.menuItems);
	}

	private updateWiFiMenu(menu: IMenuItem[], isSupported: boolean) {
		this.supportFilter(menu, 'wifi-security', isSupported);
	}

	private supportFilter(menu: IMenuItem[], id: string, isSupported: boolean) {
		menu.forEach((item) => {
			if (item.id === id) {
				item.isSupported = isSupported;
				const available = this.featureToggleService.isAvailableWithFullName(
					item.featureTag
				);
				item.hide = available === true ? !isSupported : !available;
			}
		});
		this.updateMenu();
	}

	notifyMenuChange(payload?) {
		this.commonService.sendNotification(MenuChange.MenuItemChange, payload);
	}

	updateSystemUpdatesMenu(menu) {
		const device = menu.find((item) => item.id === 'device');
		if (device !== undefined) {
			const deviceSubcategory = device.subitems.find(
				(item) => item.id === 'device-subcategory'
			);
			if (deviceSubcategory) {
				const su = deviceSubcategory.subitems.find((item) => item.id === 'system-updates');
				if (su) {
					su.hide = !this.systemUpdatePresenter.isVisible();
					this.updateMenu();
				}
			}
		}
	}

	public isSmartAssistVisible() {
		this.intelligentScreenFeatureToggleVisibility =
			this.featureToggleService.isDeviceSettingsFeatureAvailable(
				DeviceSettingsFeatureToggle.SmartAssistIntelligentScreen
			);
		this.activeProtectionSystemFeatureToggleVisibility =
			this.featureToggleService.isDeviceSettingsFeatureAvailable(
				DeviceSettingsFeatureToggle.SmartAssistActiveProtectionSystem
			);
		this.intelligentSecurityFeatureToggleVisibility =
			this.featureToggleService.isDeviceSettingsFeatureAvailable(
				DeviceSettingsFeatureToggle.SmartAssistIntelligentSecurity
			);
		const smartAssistVisibility = [
			this.intelligentScreenFeatureToggleVisibility,
			this.activeProtectionSystemFeatureToggleVisibility,
			this.intelligentSecurityFeatureToggleVisibility,
		];
		this.logger.info(
			'MenuMainComponent.isSmartAssistVisible getFeatureToggleStatus',
			smartAssistVisibility
		);
		return smartAssistVisibility.some((visibility) => visibility);
	}

	public async showSmartAssist(menu: IMenuItem[]): Promise<any> {
		this.logger.info('MenuMainComponent.showSmartAssist: inside');
		const myDeviceItem = menu.find((item) => item.id === 'device');

		if (myDeviceItem !== undefined) {
			// if cache has value true for IsSmartAssistSupported, add menu item
			const smartAssistCacheValue = this.commonService.getLocalStorageValue(
				LocalStorageKey.IsSmartAssistSupported
			);
			const hasSmartAssistVisibility = this.isSmartAssistVisible();

			this.logger.info(
				'MenuMainComponent.showSmartAssist smartAssistCacheValue',
				smartAssistCacheValue
			);

			if (smartAssistCacheValue !== undefined && hasSmartAssistVisibility) {
				this.updateSmartAssistMenu(menu, smartAssistCacheValue);
			}

			// raj: promise.all breaks if any one function is breaks. adding feature wise capability check
			const assistCapability: SmartAssistCapability = new SmartAssistCapability();

			// HPD capability check
			try {
				this.logger.info('configService.showSmartAssist: HPD capability check');
				assistCapability.isIntelligentSecuritySupported =
					await this.intelligentSecuritySection.isVisible();
				this.updateSmartAssistMenu(menu, assistCapability.isIntelligentSecuritySupported);
				this.logger.info('configService.showSmartAssist: capability check completed');
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist smartAssist.getHPDVisibility check',
					error
				);
			}
			try {
				this.logger.info('configService.showSmartAssist:AIO HPD capability check');
				assistCapability.isIntelligentSecurityAIOSupported =
					await this.intelligentSecurityAIOService.getHPDCapability();
				this.updateSmartAssistMenu(
					menu,
					assistCapability.isIntelligentSecurityAIOSupported
				);
				this.logger.info(
					'configService.showSmartAssist:AIO capability check completed',
					assistCapability.isIntelligentSecurityAIOSupported
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist intelligentSecurityAIO.getHPDCapability check',
					error
				);
			}
			// Intelligent Screen capability check
			try {
				this.logger.info(
					'configService.showSmartAssist: Intelligent Screen capability check'
				);
				const isIntelligentScreenFeaturePresent = this
					.intelligentScreenFeatureToggleVisibility
					? await this.smartAssist.getIntelligentScreenVisibility()
					: false;
				assistCapability.isIntelligentScreenSupported = isIntelligentScreenFeaturePresent;
				this.updateSmartAssistMenu(menu, assistCapability.isIntelligentScreenSupported);
				this.logger.info(
					'configService.showSmartAssist: Intelligent Screen capability check completed'
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist Intelligent Screen capability check completed check',
					error
				);
			}
			// lenovo voice capability check
			try {
				this.logger.info('configService.showSmartAssist: lenovo voice capability check');
				assistCapability.isLenovoVoiceSupported =
					await this.smartAssist.isLenovoVoiceAvailable();
				this.updateSmartAssistMenu(menu, assistCapability.isLenovoVoiceSupported);
				this.logger.info(
					'configService.showSmartAssist: lenovo voice capability check completed'
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist smartAssist.isLenovoVoiceAvailable check',
					error
				);
			}
			// video pause capability check
			try {
				this.logger.info('configService.showSmartAssist: video pause capability check');
				assistCapability.isIntelligentMediaSupported =
					await this.smartAssist.getVideoPauseResumeStatus(); // returns object
				this.updateSmartAssistMenu(
					menu,
					assistCapability.isIntelligentMediaSupported.available
				);
				this.logger.info(
					'configService.showSmartAssist: video pause capability check completed'
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist smartAssist.getVideoPauseResumeStatus check',
					error
				);
			}
			// super resolution capability check
			try {
				this.logger.info(
					'configService.showSmartAssist: super resolution capability check'
				);
				assistCapability.isSuperResolutionSupported =
					await this.smartAssist.getSuperResolutionStatus();
				this.updateSmartAssistMenu(
					menu,
					assistCapability.isSuperResolutionSupported.available
				);
				this.logger.info(
					'configService.showSmartAssist: super resolution capability check completed'
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist smartAssist.getSuperResolutionStatus check',
					error
				);
			}

			// APS capability check
			try {
				this.logger.info('configService.showSmartAssist: APS capability check');
				assistCapability.isAPSSupported = this.activeProtectionSystemFeatureToggleVisibility
					? await this.activeProtectionSystemSection.isVisible()
					: false;
				this.updateSmartAssistMenu(menu, assistCapability.isAPSSupported);
				this.logger.info(
					'MenuMainComponent.showSmartAssist: APS capability check completed'
				);
			} catch (error) {
				this.logger.exception('configService.showSmartAssist APS capability check', error);
			}

			this.isSmartAssistAvailable =
				assistCapability.isIntelligentSecuritySupported ||
				assistCapability.isIntelligentSecurityAIOSupported ||
				assistCapability.isLenovoVoiceSupported ||
				assistCapability.isIntelligentMediaSupported.available ||
				assistCapability.isIntelligentScreenSupported ||
				assistCapability.isSuperResolutionSupported.available ||
				assistCapability.isAPSSupported;

			this.updateSmartAssistMenu(menu, this.isSmartAssistAvailable);
			this.commonService.setLocalStorageValue(
				LocalStorageKey.IsSmartAssistSupported,
				this.isSmartAssistAvailable
			);
			this.commonService.setLocalStorageValue(
				LocalStorageKey.SmartAssistCapability,
				assistCapability
			);
			this.logger.error('configService.showSmartAssist capability check', {
				smartAssistCacheValue,
				isSmartAssistAvailable: this.isSmartAssistAvailable,
				assistCapability,
			});
		}
	}

	private async showSmartAssistForDesktop(menu: IMenuItem[]): Promise<any> {
		this.logger.info('MenuMainComponent.showSmartAssist: inside');
		const myDeviceItem = menu.find((item) => item.id === 'device');

		if (myDeviceItem !== undefined) {
			// if cache has value true for IsSmartAssistSupported, add menu item
			const smartAssistCacheValue = this.commonService.getLocalStorageValue(
				LocalStorageKey.IsSmartAssistSupported
			);
			const hasSmartAssistVisibility = this.isSmartAssistVisible();

			this.logger.info(
				'MenuMainComponent.showSmartAssist smartAssistCacheValue',
				smartAssistCacheValue
			);

			if (smartAssistCacheValue !== undefined && hasSmartAssistVisibility) {
				this.updateSmartAssistMenu(menu, smartAssistCacheValue);
			}
			const assistCapability: SmartAssistCapability = new SmartAssistCapability();
			try {
				this.logger.info('configService.showSmartAssist:AIO HPD capability check');
				assistCapability.isIntelligentSecurityAIOSupported =
					await this.intelligentSecuritySection.isAIOVisible();
				this.updateSmartAssistMenu(
					menu,
					assistCapability.isIntelligentSecurityAIOSupported
				);
				this.logger.info(
					'configService.showSmartAssist:AIO capability check completed',
					assistCapability.isIntelligentSecurityAIOSupported
				);
			} catch (error) {
				this.logger.exception(
					'configService.showSmartAssist intelligentSecurityAIO.getHPDCapability check',
					error
				);
			}
			this.isSmartAssistAvailable = assistCapability.isIntelligentSecurityAIOSupported;

			this.updateSmartAssistMenu(menu, this.isSmartAssistAvailable);
			this.commonService.setLocalStorageValue(
				LocalStorageKey.IsSmartAssistSupported,
				this.isSmartAssistAvailable
			);
			this.commonService.setLocalStorageValue(
				LocalStorageKey.SmartAssistCapability,
				assistCapability
			);
			this.logger.error('configService.showSmartAssist capability check', {
				smartAssistCacheValue,
				isSmartAssistAvailable: this.isSmartAssistAvailable,
				assistCapability,
			});
		}
	}

	private updateSmartAssistMenu(items: IMenuItem[], show: boolean) {
		this.isSmartAssistAvailable = this.isSmartAssistAvailable || show;
		const myDeviceItem = items.find((item) => item.id === 'device');
		if (myDeviceItem) {
			const subItem = myDeviceItem.subitems.find((item) => item.id === 'device-subcategory');
			if (subItem && subItem.subitems) {
				const smartAssistItem = subItem.subitems.find((item) => item.id === 'smart-assist');
				if (smartAssistItem) {
					smartAssistItem.hide = !this.isSmartAssistAvailable;
					this.updateMenu();
				}
			}
		}
	}

	private smartAssistFilter(machineType: number, menu: IMenuItem[]) {
		if (machineType === 0 || machineType === 1) {
			this.showSmartAssist(menu);
		} else if (machineType === 4) {
			this.showSmartAssistForDesktop(menu);
		} else {
			this.updateSmartAssistMenu(menu, false);
		}
	}

	private async initSmartAssistMenu() {
		this.logger.info('initializeSmartAssist: enter');
		this.updateSmartAssistMenu(this.menuItems, false);
		this.logger.error('initializeSmartAssist: enter', this.menuItems);
		let machineType = this.commonService.getLocalStorageValue(
			LocalStorageKey.MachineType,
			undefined
		);
		if (machineType !== undefined) {
			this.smartAssistFilter(machineType, this.menuItems);
		} else if (this.deviceService.isShellAvailable) {
			machineType = await this.deviceService.getMachineType();
			this.smartAssistFilter(machineType, this.menuItems);
		}
		this.logger.info('initializeSmartAssist: leave');
	}

	private applyADPolicy(menu: IMenuItem[]) {
		menu.forEach((item) => {
			if (item.hide === false) {
				if (item.featureTag) {
					const available = this.featureToggleService.isAvailableWithFullName(
						item.featureTag
					);

					if (typeof available === 'boolean') {
						item.hide = !available;
					}
				}

				if (item.subitems.length > 0) {
					this.applyADPolicy(item.subitems);
				}
			}
		});
	}

	private configureMenuPath(menu: IMenuItem[]): IMenuItem[] {
		const finalMenu = cloneDeep(menu);

		finalMenu.forEach((item) => {
			item.path = item.path ? `/${item.path}` : '';
			if (item.subitems.length > 0) {
				item.subitems.forEach((subitem) => {
					if (subitem.path) {
						subitem.path = `${item.path}/${subitem.path}`;
					} else {
						subitem.path = item.path;
					}

					if (subitem.subitems.length > 0) {
						subitem.subitems.forEach((thirdItem) => {
							if (!thirdItem.isFullPath) {
								if (thirdItem.path) {
									if (thirdItem.outletLink) {
										thirdItem.path = `${subitem.path}`;
									} else {
										thirdItem.path = `${subitem.path}/${thirdItem.path}`;
									}
								} else {
									thirdItem.path = subitem.path;
								}
							}
						});
					}
				});
				if (item.hide === false) {
					item.path = this.getParentMenuPath(item);
				}
			}
		});
		return finalMenu;
	}

	getParentMenuPath(menu: IMenuItem): string {
		let path = menu.path;
		const subitems = menu.subitems;
		if (subitems.length === 0) {
			return path;
		}
		const defaultSubRoute = subitems.filter((subitem) => subitem.path === '')[0];
		if (defaultSubRoute && defaultSubRoute.hide === false) {
			return path;
		}
		for (const subitem of subitems) {
			if (subitem.hide === false) {
				path = subitem.path ? `${subitem.path}` : path;
				break;
			}
		}
		return path;
	}

	private flatMenuItems(menu: IMenuItem[]): IMenuItem[] {
		let flattedMenu = [];
		menu.forEach((item) => {
			flattedMenu.push(item);
			if (item.subitems.length > 0) {
				flattedMenu = flattedMenu.concat(this.flatMenuItems(item.subitems));
			}
		});

		return flattedMenu;
	}

	chooseDefaultRoute(menu: IMenuItem[]) {
		let flatMenu = this.flatMenuItems(menu);
		flatMenu = flatMenu.filter((item) => item.before !== undefined);
		flatMenu = flatMenu.filter((item) => item.hide !== true);
		let candidateItems = flatMenu.filter((item) => item.before === '').map((item) => [item]);
		candidateItems = candidateItems.concat(
			flatMenu.filter((item) => item.before !== '').map((item) => [item])
		);

		candidateItems.forEach((tempArray, tempIndex) => {
			if (tempArray.length < 1 || tempArray[0].before === '') {
				return;
			}

			for (let index = 0; index < candidateItems.length; index++) {
				if (tempIndex === index || !candidateItems[index]) {
					continue;
				}
				const frontIndex = candidateItems[index].findIndex((item) => {
					return (
						item &&
						tempArray &&
						item.featureTag === tempArray[0].before &&
						item.featureTag !== tempArray[0].featureTag
					);
				});

				if (frontIndex !== -1) {
					const secondHalf = candidateItems[tempIndex].splice(frontIndex + 1);
					candidateItems[index] = candidateItems[index]
						.concat(tempArray)
						.concat(secondHalf);
					candidateItems[tempIndex] = null;
					break;
				}
			}
		});

		this.defaultRoute = this.router.parseUrl('/');
		candidateItems = candidateItems.filter((item) => item !== null);
		if (
			candidateItems.length > 0 &&
			candidateItems[0][0] &&
			candidateItems[0][0].hide !== undefined
		) {
			this.defaultRoute = this.router.parseUrl(candidateItems[0][0].path);
			this.defaultRouteSubject.next(this.defaultRoute);
		}
	}

	isBelongToDefaultRoute(url: string): boolean {
		return this.removeOutletFromUrl(url) === this.defaultRoute.toString();
	}

	private removeOutletFromUrl(origin: string): string {
		return origin.split('/(')[0];
	}

	public getNextDeviceSettingsPageUrl(currentPage: EDeviceSettingsPages) {
		const pageOrder = [
			EDeviceSettingsPages.Power,
			EDeviceSettingsPages.Audio,
			EDeviceSettingsPages.DisplayAndCamera,
			EDeviceSettingsPages.InputAndAccessories,
		];
		const deviceSettingsSubMenuItems = this.getDeviceSettingsMenu(
			this.menuItems
		)?.subitems?.filter((item) => item.hide === false) as IMenuItem[];

		const currentPageIndex = pageOrder.findIndex((obj) => obj === currentPage);
		let pageUrl: UrlTree;
		for (let counter = 0; counter < pageOrder.length; counter++) {
			const nextPageIndex = (currentPageIndex + counter) % pageOrder.length;
			const pageCandidate = deviceSettingsSubMenuItems.find(
				(obj) => obj.id === pageOrder[nextPageIndex]
			);
			if (pageCandidate) {
				const outletLinkPath = pageCandidate.outletLink[0];
				const outletTabPath = pageCandidate.outletLink[1].outlets.tab[0];
				const url = `${outletLinkPath}/(tab:${outletTabPath})`;
				pageUrl = this.router.parseUrl(url);
				break;
			}
		}

		return pageUrl || this.defaultRoute;
	}
}
